import React from "react";
import PatientsListRow from "./PatientsListRow";
import PatientsListEmptyState from "./PatientsListEmptyState";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronUp, ChevronDown, Database } from "lucide-react";
import "@/assets/css/PatientListView.scss";
import { Patient } from "@/lib/patient/patientListviewData";
import { Button } from "@/components/ui/button";

type SortField = "name" | "age" | "address" | "lastConsultation" | "assignee";
type SortDirection = "asc" | "desc";

interface PatientsListTableProps {
  patients: Patient[];
  isDarkMode: boolean;
  currentPharmacyId: string;
  onPatientClick: (patient: Patient) => void;
  onConsultationClick?: (patient: Patient) => void;
  onAddPatient: () => void;
  selectAll: boolean;
  onSelectAll: (checked: boolean) => void;
  selectedPatients: Set<string>;
  onSelectPatient: (patientId: string, checked: boolean) => void;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort: (field: SortField) => void;
  handleImportClick: () => void;
  loadingRowId: string;
  searchValue?: string;
  includeTitanPatients?: boolean;
  onIncludeTitanPatientsChange?: (value: boolean) => void;
  searchTitanEnabled?: boolean;
  setTriggerTitanSearch: (value: boolean) => void;
  setShowPermissionMessage?: (message: string) => void;
  setShowPermissionModal?: (value: boolean) => void;
}

export default function PatientsListTable({
  patients,
  isDarkMode,
  currentPharmacyId,
  onPatientClick,
  onConsultationClick,
  onAddPatient,
  selectAll,
  onSelectAll,
  selectedPatients,
  onSelectPatient,
  sortField,
  sortDirection,
  onSort,
  handleImportClick,
  loadingRowId,
  searchValue,
  includeTitanPatients,
  onIncludeTitanPatientsChange,
  searchTitanEnabled = false,
  setTriggerTitanSearch,
  setShowPermissionMessage,
  setShowPermissionModal,
}: PatientsListTableProps) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const PAGE_SIZE = 100;

  const stableOnSelectPatient = React.useCallback(
    (patientId: string, checked: boolean) => onSelectPatient(patientId, checked),
    [onSelectPatient],
  );
  const totalPages = Math.ceil(patients.length / PAGE_SIZE);

  const paginatedPatients = React.useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return patients.slice(start, start + PAGE_SIZE);
  }, [patients, currentPage]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [patients, sortField, sortDirection]);

  const SortableHeader = ({
    field,
    children,
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <button
      onClick={() => onSort(field)}
      className={`flex items-center gap-1 font-normal transition-colors duration-300 ${
        isDarkMode
          ? "text-gray-100 hover:text-gray-200"
          : "text-gray-500 hover:text-gray-900"
      }`}
    >
      {children}

      {sortField === field ? (
        sortDirection === "asc" ? (
          <ChevronUp
            className={`size-4 ${isDarkMode ? "stroke-white" : "stroke-black"}`}
          />
        ) : (
          <ChevronDown
            className={`size-4 ${isDarkMode ? "stroke-white" : "stroke-black"}`}
          />
        )
      ) : (
        <ChevronUp
          className={`size-3 opacity-3 ${
            isDarkMode ? "stroke-white" : "stroke-black"
          }`}
        />
      )}
    </button>
  );
  return (
    <div
      className={`flex-1 rounded-lg border overflow-hidden transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div
        className={`${
          totalPages === 1 || paginatedPatients.length === 0
            ? "h-[calc(100vh-190px)]"
            : "h-[calc(100vh-245px)]"
        } overflow-auto patient-listview-table`}
      >
        <table className="w-full">
          <thead
            className={`sticky top-0 z-10 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <tr>
              <th className="py-3 pl-3 pr-1">
                <Checkbox
                  checked={selectAll}
                  onCheckedChange={(checked) => onSelectAll(!!checked)}
                />
              </th>

              <th className="py-3 pl-1 pr-6 text-xs tracking-wider uppercase">
                <SortableHeader field="name">Patient</SortableHeader>
              </th>

              <th className="px-6 py-3 text-xs tracking-wider uppercase">
                <SortableHeader field="age">Age</SortableHeader>
              </th>

              <th className="px-6 py-3 text-xs tracking-wider uppercase">
                <SortableHeader field="address">Address</SortableHeader>
              </th>

              <th
                className={`px-6 py-3 text-xs font-normal tracking-wider uppercase ${
                  isDarkMode ? "text-white" : "text-gray-500"
                }`}
              >
                Tags
              </th>

              <th className="px-6 py-3 text-xs tracking-wider uppercase">
                <SortableHeader field="lastConsultation">
                  Last Consultation
                </SortableHeader>
              </th>

              <th className="px-6 py-3 text-xs tracking-wider uppercase">
                <SortableHeader field="practitioner">
                  Practitioner
                </SortableHeader>
              </th>

              <th
                className={`px-6 py-3 text-xs font-normal tracking-wider uppercase ${
                  isDarkMode ? "text-white" : "text-gray-500"
                }`}
              >
                PDS Status
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedPatients.map((patient) => (
              <PatientsListRow
                key={patient.id}
                patient={patient}
                isDarkMode={isDarkMode}
                currentPharmacyId={currentPharmacyId}
                onPatientClick={onPatientClick}
                onConsultationClick={onConsultationClick}
                isSelected={selectedPatients.has(patient.id)}
                onSelectPatient={stableOnSelectPatient}
                handleImportClick={handleImportClick}
                loadingRowId={loadingRowId}
              />
            ))}
          </tbody>
        </table>

        {patients.length === 0 && (
          <PatientsListEmptyState
            isDarkMode={isDarkMode}
            onAddPatient={onAddPatient}
            searchValue={searchValue}
            includeTitanPatients={includeTitanPatients}
            onIncludeTitanPatientsChange={onIncludeTitanPatientsChange}
            searchTitanEnabled={searchTitanEnabled}
            setTriggerTitanSearch={setTriggerTitanSearch}
            setShowPermissionMessage={setShowPermissionMessage}
            setShowPermissionModal={setShowPermissionModal}
          />
        )}

        {/* Expand to Titan button - appears after last patient when searching */}
        {patients.length > 0 &&
          searchValue &&
          searchValue.trim() !== "" &&
          !includeTitanPatients &&
          onIncludeTitanPatientsChange && (
            <div
              className={`p-4 border-t text-center ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => onIncludeTitanPatientsChange(true)}
                className={
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }
              >
                <Database className="w-4 h-4 mr-2" />
                Expand to Titan
              </Button>
              <p
                className={`text-xs mt-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Search Titan database for more results
              </p>
            </div>
          )}
      </div>

      {totalPages > 1 && (
        <div
          className={`flex items-center justify-between px-4 py-3 border-t ${
            isDarkMode
              ? "border-gray-700 text-gray-300"
              : "border-gray-200 text-gray-600"
          }`}
        >
          <span className="text-sm">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–
            {Math.min(currentPage * PAGE_SIZE, patients.length)} of{" "}
            {patients.length}
          </span>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border rounded disabled:opacity-40"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (page) =>
                  page === 1 ||
                  page === totalPages ||
                  Math.abs(page - currentPage) <= 1,
              )
              .map((page, idx, arr) => (
                <React.Fragment key={page}>
                  {idx > 0 && page - arr[idx - 1] > 1 && (
                    <span className="px-1">…</span>
                  )}
                  <button
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm rounded ${
                      page === currentPage ? "bg-blue-600 text-white" : "border"
                    }`}
                  >
                    {page}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border rounded disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
